// src/components/PhotoSharing/SharePhotosButton.tsx
import React, { useState } from 'react';
import { Button, ButtonProps, Snackbar, Alert } from '@mui/material';
import { Share as ShareIcon } from '@mui/icons-material';
import PhotoSharingModal from './PhotoSharingModal';
import { useCanSharePhotos, EntityData } from '../../utils/photoSharingPermissions';

interface SharePhotosButtonProps extends Omit<ButtonProps, 'onClick'> {
   entityType: 'trip' | 'event' | 'album';
   entityId: string;
   entityTitle: string;
   selectedPhotos?: any[];
   entityData?: EntityData | null;
   onSuccess?: (result: any) => void;
}

const SharePhotosButton: React.FC<SharePhotosButtonProps> = ({
   entityType,
   entityId,
   entityTitle,
   selectedPhotos,
   entityData,
   onSuccess,
   children,
   ...buttonProps
}) => {
   const [modalOpen, setModalOpen] = useState(false);
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');

   // Check user permissions using the hook
   const canShare = useCanSharePhotos(entityData || null);

   // Don't render button if user can't share (is viewer)
   if (!canShare) {
      return null;
   }

   const handleSuccess = (result: any) => {
      const totalEmails =
         (result.emailsSent?.participants?.length || 0) +
         (result.emailsSent?.nonParticipants?.length || 0);

      setSnackbarMessage(
         `Photos shared successfully with ${totalEmails} recipient${totalEmails !== 1 ? 's' : ''}!`
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      onSuccess?.(result);
   };

   const handleError = (error: any) => {
      setSnackbarMessage(error.message || 'Failed to share photos');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
   };

   return (
      <>
         <Button {...buttonProps} onClick={() => setModalOpen(true)} startIcon={<ShareIcon />}>
            {children || 'Share Photos'}
         </Button>

         <PhotoSharingModal
            open={modalOpen}
            onClose={() => setModalOpen(false)}
            entityType={entityType}
            entityId={entityId}
            entityTitle={entityTitle}
            selectedPhotos={selectedPhotos}
            entityData={entityData}
            onSuccess={handleSuccess}
         />

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </>
   );
};

export default SharePhotosButton;


